import { parseFrontMatter } from './frontMatter.ts';
import { normalizeAnchorId } from './html.ts';
import type { NovelSection } from './types.ts';

export function isDividerComment(commentText: string): boolean {
  return /^[=\-\s]+$/.test(commentText.trim());
}

export function isParserDirective(commentText: string): boolean {
  return /^(?:pagebreak|novel-anchor|novel-toc-link)\b/i.test(commentText.trim());
}

function extractLegacySectionTitle(line: string): string | null {
  const comments = [...line.matchAll(/<!--\s*([\s\S]*?)\s*-->/g)]
    .map((match) => match[1]?.trim() || '')
    .filter(Boolean);

  if (comments.length < 3) {
    return null;
  }

  const title = comments.find(
    (comment) => !isDividerComment(comment) && !isParserDirective(comment),
  );
  return title ? title.replace(/^#+\s*/, '').trim() : null;
}

/**
 * 小説の本文をセクション（章やページ）に分割します。
 * HTMLコメント区切り、マークダウンの水平線、見出し、または章/ページを表すキーワード（「第一章」「ページ1」など）で分割します。
 */
export function splitIntoSections(content: string): NovelSection[] {
  // フロントマターが存在する場合は取り除く
  const { content: cleanContent } = parseFrontMatter(content);
  const lines = cleanContent.split(/\r?\n/);
  const sections: { title: string; lines: string[]; anchorId?: string }[] = [];
  let currentTitle = '表紙・はじめに';
  let currentLines: string[] = [];
  let currentAnchorId: string | undefined;
  let pendingAnchorId: string | undefined;
  let currentHasContent = false;

  const pageBreakRegex = /<!--\s*pagebreak(?:\s*:\s*([^>\s]+))?\s*-->/i;
  const anchorRegex = /^<!--\s*novel-anchor\s*:\s*([^>]+?)\s*-->$/i;
  const titleCommentRegex = /^<!--\s*(.+?)\s*-->$/;

  const pushCurrentSection = () => {
    sections.push({
      title: currentTitle,
      lines: currentLines,
      anchorId: currentAnchorId,
    });
  };

  const episodeToChapterMap = new Map<string, string>();
  let currentChapter = '';
  let inTOC = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // 高速パス: 目次外であり、かつコメントを含まない地の文や空行を高速処理
    if (!inTOC && !trimmed.includes('<!--') && trimmed !== '## 目次') {
      currentLines.push(line);
      if (!currentHasContent && trimmed.length > 0) {
        currentHasContent = true;
      }
      continue;
    }

    if (trimmed === '## 目次') {
      inTOC = true;
    } else if (inTOC && pageBreakRegex.test(trimmed)) {
      inTOC = false;
    }

    if (inTOC) {
      if (trimmed.startsWith('### ')) {
        currentChapter = trimmed.replace(/^###\s*/, '').trim();
      }
      const tocLinkMatch = trimmed.match(
        /<!--\s*novel-toc-link\s*:\s*([^|>]+?)\s*\|\s*([\s\S]*?)\s*-->/i,
      );
      if (tocLinkMatch && currentChapter) {
        const anchorId = normalizeAnchorId(tocLinkMatch[1]);
        episodeToChapterMap.set(anchorId, currentChapter);
      }
    }

    if (trimmed.includes('<!--')) {
      const legacyTitle = extractLegacySectionTitle(trimmed);
      if (legacyTitle) {
        if (currentHasContent) {
          pushCurrentSection();
        }

        currentTitle = legacyTitle;
        currentAnchorId = pendingAnchorId || normalizeAnchorId(legacyTitle);
        pendingAnchorId = undefined;
        currentLines = [];
        currentHasContent = false;
        continue;
      }
    }

    if (trimmed.startsWith('<!--')) {
      const anchorMatch = trimmed.match(anchorRegex);
      if (anchorMatch) {
        pendingAnchorId = normalizeAnchorId(anchorMatch[1]);
        continue;
      }

      const pageBreakMatch = trimmed.match(pageBreakRegex);
      const titleCommentMatch = trimmed.match(titleCommentRegex);

      if (pageBreakMatch) {
        if (currentHasContent) {
          pushCurrentSection();
          currentLines = [];
          currentHasContent = false;
        }

        const rawTitle = pageBreakMatch[1];
        currentAnchorId = pendingAnchorId || (rawTitle ? normalizeAnchorId(rawTitle) : undefined);
        pendingAnchorId = undefined;
        if (rawTitle) {
          const normalizedTitle = rawTitle.replace(/^[\\/]+|[\\/]+$/g, '').trim();
          const numericMatch = normalizedTitle.match(/(\d+)/);
          currentTitle =
            /^episode-\d+$/i.test(normalizedTitle) && numericMatch
              ? numericMatch[1]
              : normalizedTitle;
        } else if (!currentTitle || currentTitle === '表紙・はじめに') {
          currentTitle = `第 ${sections.length + 1} ページ`;
        }
        continue;
      }

      if (titleCommentMatch) {
        const commentText = titleCommentMatch[1].trim();
        if (isDividerComment(commentText)) {
          currentLines.push(line);
          if (!currentHasContent) {
            if (
              trimmed.length > 0 &&
              !titleCommentRegex.test(trimmed) &&
              !anchorRegex.test(trimmed)
            ) {
              currentHasContent = true;
            }
          }
        } else if (commentText && !isParserDirective(commentText)) {
          currentTitle = commentText.replace(/^#+\s*/, '').trim();
        }
        continue;
      }
    }

    currentLines.push(line);
    if (!currentHasContent) {
      if (trimmed.length > 0 && !titleCommentRegex.test(trimmed) && !anchorRegex.test(trimmed)) {
        currentHasContent = true;
      }
    }
  }

  if (currentLines.length > 0 || sections.length === 0) {
    pushCurrentSection();
  }

  return sections.map((sec, index) => {
    const body = sec.lines.join('\n').trim();
    let displayTitle = sec.title.replace(/^#+\s*/, '').trim();

    if (index === 0 && displayTitle === '表紙・はじめに') {
      const firstHeadingMatch = body.match(/^#\s+(.+)$/m);
      if (firstHeadingMatch?.[1]) {
        displayTitle = firstHeadingMatch[1].trim();
      }
    }

    const anchorId = sec.anchorId ? normalizeAnchorId(sec.anchorId) : undefined;
    const chapterTitle = anchorId ? episodeToChapterMap.get(anchorId) : undefined;

    return {
      id: anchorId || `section-${index}`,
      anchorId,
      title: displayTitle,
      content: body,
      chapterTitle,
    };
  });
}
